/**
 * The browser's source of Bearer tokens for `/api/v1/*` (TX-4856).
 *
 * ## Why a registry rather than a hook
 *
 * `useAuth().getToken()` is a React hook, and `apiRequest` is a plain function
 * called from query functions, event handlers and modules that are not
 * components. The token has to reach it somehow, and threading it through every
 * caller would mean touching every `hooks/<domain>/*Api.ts` and every call site
 * — a large diff whose size is entirely accidental.
 *
 * So a provider component registers Clerk's `getToken` once, and `apiRequest`
 * asks for it. One place knows about Clerk; everything else asks for a string.
 *
 * ## Why this exists at all
 *
 * Web is the only client still authenticating with a cookie — the extension
 * and Swift both send Bearer already. A cookie cannot cross an origin,
 * so the API cannot move to its own domain until this does. Nothing
 * user-visible changes here: requests stay same-origin and the cookie still
 * works. This is the unlock, not the move.
 */

export type ApiTokenGetter = (options?: {
  /** Bypass Clerk's cache — used once after a 401, never on the happy path. */
  skipCache?: boolean;
}) => Promise<string | null>;

let getter: ApiTokenGetter | null = null;

/** Registered by `ApiTokenProvider`. Passing `null` clears it, for tests. */
export function setApiTokenGetter(next: ApiTokenGetter | null): void {
  getter = next;
}

/**
 * The current token, or `null`.
 *
 * Returns `null` rather than throwing when nothing is registered: server-side
 * rendering, a signed-out visitor and the moment before the provider mounts are
 * all legitimate, and an unauthenticated request answering 401 is a better
 * failure than a crash in a query function.
 */
export async function getApiToken(options?: {
  skipCache?: boolean;
}): Promise<string | null> {
  if (!getter) return null;
  try {
    return await getter(options);
  } catch {
    // A token fetch that fails is the same case as no token: let the request go
    // out and let the API answer. Throwing here would turn a transient Clerk
    // hiccup into a broken page.
    return null;
  }
}

/**
 * How long a request will wait for Clerk before giving up and going out
 * unauthenticated.
 *
 * Measured on the preview: Clerk reports loaded about **400ms after first
 * paint** (p50 415ms, p90 714ms across twelve cold loads). Waiting therefore
 * costs roughly four tenths of a second of LOADING STATE on the first request
 * of a session — paint itself does not wait on an API call and is unchanged.
 *
 * The ceiling is what stops that becoming a hang. If Clerk never loads — an
 * outage, a blocked script, a browser extension — requests go out without a
 * token after this and the API answers 401, which is a page that says it needs
 * a sign-in rather than a page that never renders.
 */
export const CLERK_READY_TIMEOUT_MS = 10_000;

function clerkLoaded(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as unknown as { Clerk?: { loaded?: boolean } }).Clerk?.loaded);
}

/**
 * Registers a getter that waits for Clerk, for the window before the provider
 * mounts.
 *
 * The waiting lives HERE rather than inside `getApiToken` for a reason worth
 * recording: putting it there made every caller wait, including server
 * rendering and every jsdom test that touches `apiRequest` — five unrelated
 * suites went from milliseconds to the full ceiling. A wait that only exists
 * where Clerk does is the same behaviour without that blast radius.
 *
 * Installed at module load in the browser, so the opening burst of a page load
 * is covered. `ApiTokenProvider` replaces it with the `useAuth` version once it
 * mounts.
 */
export function installClerkBootstrapTokenGetter(): void {
  if (typeof window === "undefined") return;
  if (getter) return;
  setApiTokenGetter(async (options) => {
    await waitForClerk();
    const source = clerkGlobalGetter();
    return source ? source(options) : null;
  });
}

/**
 * Waits for Clerk, bounded.
 *
 * Without this, the opening burst of a page load — measured at 14 requests per
 * page — goes out before Clerk exists and carries no token at all. Same-origin
 * they still succeed on the cookie, which is why nothing had noticed; once the
 * API is on its own origin they would simply 401. Waiting is what makes "every
 * request carries a token" true rather than nearly true.
 */
async function waitForClerk(): Promise<void> {
  if (typeof window === "undefined") return;
  if (clerkLoaded()) return;
  await new Promise<void>((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      if (clerkLoaded() || Date.now() - startedAt > CLERK_READY_TIMEOUT_MS) {
        resolve();
        return;
      }
      setTimeout(tick, 25);
    };
    tick();
  });
}

/**
 * Clerk's browser global, used when the provider has not registered yet.
 *
 * The provider registers in an effect, and TanStack queries fire from effects
 * too — so the very first requests of a page load can race it. That is not
 * hypothetical: an e2e sweep of four pages found **14 requests per page** going
 * out with no `Authorization` header, all of them from that window. They still
 * worked, because same-origin requests carry the cookie, which is exactly why
 * nothing had noticed and exactly what breaks the moment the API moves origin.
 *
 * Reading the global removes the ordering dependency altogether rather than
 * trying to win the race by mounting the provider earlier. The provider is
 * still what the app uses once mounted; this is the floor.
 */
function clerkGlobalGetter(): ApiTokenGetter | null {
  if (typeof window === "undefined") return null;
  const clerk = (window as unknown as {
    Clerk?: { session?: { getToken?: (o?: { skipCache?: boolean }) => Promise<string | null> } };
  }).Clerk;
  const getToken = clerk?.session?.getToken;
  if (!getToken || !clerk?.session) return null;
  return (options) => getToken.call(clerk.session, options);
}

/** Whether a token source is available at all. For diagnostics and tests. */
export function hasApiTokenGetter(): boolean {
  return getter !== null;
}
